import { type ToolRendererProps } from '@multiplayer-app/ai-agent-react'
import { AgentToolCallStatus } from '@multiplayer-app/ai-agent-types'
import { clip, formatCount } from './format'

/** One human line per document read tool call: what is being / was read. */
function describeReadCall(
  name: string,
  input: Record<string, unknown>,
  output: Record<string, unknown> | undefined,
  running: boolean
): string {
  switch (name) {
    case 'getWordDocumentOutline': {
      if (running || !output) return 'Reading the document outline…'
      const headings = Array.isArray(output.headings) ? output.headings.length : 0
      return `Read the outline — ${headings} headings, ${formatCount(output.totalParagraphs)} paragraphs`
    }
    case 'readWordParagraphs': {
      const range = output?.returnedRange as { start?: number; end?: number } | undefined
      if (running || !range) return `Reading paragraphs ${input.start ?? '?'}–${input.end ?? '?'}…`
      return `Read paragraphs ${range.start}–${range.end} of ${formatCount(output?.totalParagraphs)}`
    }
    case 'searchWordDocumentText':
    case 'searchExcelCells': {
      const query = typeof input.query === 'string' ? clip(input.query, 48) : ''
      if (running || !output) return `Searching for “${query}”…`
      return `Searched for “${query}” — ${formatCount(output.totalMatches)} matches`
    }
    case 'getExcelWorkbookOutline': {
      if (running || !output) return 'Reading the workbook structure…'
      const sheets = Array.isArray(output.sheets) ? output.sheets.length : 0
      return `Read the workbook structure — ${sheets} sheets`
    }
    case 'getCurrentOfficeSelection': {
      if (running || !output) return 'Checking the current selection…'
      return output.selection ? 'Checked the current selection' : 'Checked — nothing is selected'
    }
    case 'readExcelRows': {
      const sheet = typeof input.sheetName === 'string' ? input.sheetName : ''
      const range = output?.returnedRange as { startRow?: number; endRow?: number } | undefined
      if (running || !range) return `Reading ${sheet} rows ${input.startRow ?? '?'}–${input.endRow ?? '?'}…`
      return `Read ${sheet} rows ${range.startRow}–${range.endRow} of ${formatCount(output?.totalRows)}`
    }
    default:
      return running ? 'Reading the document…' : 'Read the document'
  }
}

/**
 * Minimal activity line for the server-side document read tools — shows what
 * the agent is reading instead of a generic tool-call block. Read results are
 * model context, not user content, so nothing expandable is rendered.
 */
export function OfficeReadRenderer({ call }: ToolRendererProps) {
  const running = call.status === AgentToolCallStatus.Pending || call.status === AgentToolCallStatus.Running
  const output = call.output as Record<string, unknown> | undefined
  // Structured mirror-miss (output.error) is a soft fallback, not a failure.
  const notSynced = typeof output?.error === 'string'
  const failed = call.status === AgentToolCallStatus.Failed

  let text = describeReadCall(call.name, call.input ?? {}, output, running)
  if (notSynced) text = 'Document not synced — answered from the snapshot instead'
  if (failed) text = 'Document read failed'

  return (
    <div className={`ramp-read${running ? ' ramp-read-active' : ''}${failed || notSynced ? ' ramp-read-warn' : ''}`}>
      <span className='ramp-read-dot' aria-hidden />
      <span>{text}</span>
    </div>
  )
}
